/**
 * @since 2025-02-19
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import PanelScheduler from "components/PanelScheduler";
import { useNotification } from "components/Shared/Notifications/Notifications";
import TextCopyright from "components/TextCopyright";
import "firebaseConfig"; // ensure Firebase is initialized when enabled
import { getAuth } from "firebase/auth";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useAuthenticationService,
  LoginComponent,
  getAuthConfig,
  createMsalConfig,
  getInitialProvider,
  needsAuthentication
} from "@algotraders/web-auth";
import "./App.scss";

import { getConfig } from "../../getConfig";
const config = getConfig();

const App = () => {
  const { notifySuccess, notifyError } = useNotification();

  // Parse configuration using shared utilities
  const authConfig = useMemo(() => getAuthConfig(config), []);

  const firebaseAuth = useMemo(() => {
    try {
      return getAuth();
    } catch {
      return null;
    }
  }, []);

  const azureMsalConfig = useMemo(() => createMsalConfig(authConfig), [authConfig]);

  const [selectedProvider, setSelectedProvider] = useState(() => getInitialProvider(authConfig));

  const {
    user,
    idToken,
    isLoading,
    isAuthenticated,
    signOut,
    loginPopup,
    getToken,
  } = useAuthenticationService({
    provider: selectedProvider,
    firebaseAuth: authConfig.firebase.enabled ? firebaseAuth : null,
    msalConfig: authConfig.azure.enabled ? azureMsalConfig : null,
    backendUrl: `${config.BACKEND_HTTP_URL}/api`,
    tokenRefreshInterval: 50 * 60 * 1000,
    onError: async (e) => {
      console.error('Authentication error:', e);
      notifyError(e?.message || 'Authentication error');
      // Trigger logout to return to login screen
      try {
        await signOut();
        try { localStorage.removeItem('authProvider'); } catch {}
      } catch {}
    },
  });

  // Save the selected provider to localStorage when it changes
  useEffect(() => {
    if (selectedProvider) {
      try {
        localStorage.setItem('authProvider', selectedProvider);
      } catch {}
    }
  }, [selectedProvider]);




  const handleLogout = useCallback(async (reason = null) => {
    try {
      // Call backend logout endpoint to log compliance event
      if (idToken) {
        try {
          await fetch(`${config.BACKEND_HTTP_URL}/api/logout/`, {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
          });
        } catch {}
      }
      await signOut();
      try { localStorage.removeItem('authProvider'); } catch {}
      // Show the reason if provided (auth error), otherwise show success message
      if (reason) {
        notifyError(reason);
      } else {
        notifySuccess("Successfully logged out");
      }
    } catch (e) {
      notifyError("Error during logout");
    }
  }, [signOut, notifySuccess, notifyError, idToken]);

  const handleFirebaseLogin = useCallback(async () => {
    setSelectedProvider('firebase');
    try { localStorage.setItem('authProvider', 'firebase'); } catch {}
    
    // Wait for the provider switch and service re-creation to complete
    setTimeout(async () => {
      try {
        if (firebaseAuth && authConfig.firebase.enabled) {
          const { signInWithPopup, GoogleAuthProvider } = await import('firebase/auth');
          const provider = new GoogleAuthProvider();
          await signInWithPopup(firebaseAuth, provider);
        }
      } catch (error) {
        console.error('Firebase login error:', error);
        notifyError('Firebase login failed');
      }
    }, 500);
  }, [firebaseAuth, notifyError]);

  const handleAzureLogin = useCallback(async () => {
    setSelectedProvider('azure');
    try { localStorage.setItem('authProvider', 'azure'); } catch {}
    setTimeout(() => {
      loginPopup?.();
    }, 100);
  }, [loginPopup]);

  // Support forced logout via URL path: /logout
  useEffect(() => {
    if (window.location.pathname === "/logout") {
      (async () => {
        try {
          // Call backend logout endpoint to log compliance event
          if (idToken) {
            try {
              await fetch(`${config.BACKEND_HTTP_URL}/api/logout/`, {
                method: "POST",
                headers: {
                  "Authorization": `Bearer ${idToken}`,
                  "Content-Type": "application/json",
                },
              });
            } catch {}
          }
          await signOut();
          notifySuccess("Successfully logged out");
        } catch (e) {
          notifyError("Error during logout");
        } finally {
          // Ensure we land on the login page/root after logout
          window.location.replace("/");
        }
      })();
    }
  }, [signOut, notifySuccess, notifyError, idToken]);

  // Default to requiring authentication if flags are missing
  const authRequired = needsAuthentication(authConfig);

  // For Azure auth, if we have a user but no token yet, it might be in the process of silent token acquisition
  // This happens when refreshing the page - the cached user is restored first, then the token is acquired
  const waitingForAzureToken = selectedProvider === 'azure' && user && !idToken;
  
  // Also check if we might have a cached session that's being restored
  const mightHaveCachedSession = selectedProvider === 'azure' && !user && !idToken && isLoading;
  
  // Always show loading state for a brief moment on initial load to prevent login screen flash
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // Track if authentication has timed out (stuck on Authenticating...)
  const [authTimedOut, setAuthTimedOut] = useState(false);

  useEffect(() => {
    // Give Azure auth service time to check for cached accounts
    const timer = setTimeout(() => {
      setInitialLoadComplete(true);
    }, 1500); // Wait 1.5 seconds before showing any UI
    return () => clearTimeout(timer);
  }, []);

  // Timeout for authentication - if we're stuck on Authenticating for too long, show logout option
  useEffect(() => {
    // Only start timeout after initial load is complete
    if (!initialLoadComplete) return;

    // If we're authenticated, no need for timeout
    if (isAuthenticated) {
      setAuthTimedOut(false);
      return;
    }

    // Set a timeout - if still showing Authenticating after 5 seconds, allow manual logout
    const timeout = setTimeout(() => {
      setAuthTimedOut(true);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [initialLoadComplete, isAuthenticated]);

  // Handle force logout when stuck on authenticating
  const handleForceLogout = useCallback(async () => {
    try {
      // Clear all auth-related localStorage
      localStorage.removeItem('authProvider');
      localStorage.removeItem('azure_user');
      localStorage.removeItem('azure_token');
      await signOut();
      // Reset timeout state
      setAuthTimedOut(false);
    } catch (e) {
      console.error('Error during force logout:', e);
      // Force reload as fallback
      window.location.reload();
    }
  }, [signOut]);

  // Show loading spinner while authentication state is being determined
  // For Azure, we need to wait longer to avoid showing login screen during token restoration
  const showAuthenticating = !initialLoadComplete || isLoading || waitingForAzureToken || mightHaveCachedSession;

  // Reset authTimedOut when we're no longer showing the authenticating screen
  useEffect(() => {
    if (!showAuthenticating && authTimedOut) {
      setAuthTimedOut(false);
    }
  }, [showAuthenticating, authTimedOut]);

  if (authRequired && showAuthenticating) {
    return (
      <>
        <div className="app-container">
          <div className="content">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
                fontFamily: "Maven Pro, Arial, sans-serif",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  color: "white",
                  fontFamily: "Maven Pro, Arial, sans-serif",
                  fontWeight: "normal",
                  lineHeight: "1.2",
                  textAlign: "center",
                  margin: "0",
                  padding: "0"
                }}
              >
                Authenticating...
              </div>
              {authTimedOut && (
                <div style={{ marginTop: "24px", textAlign: "center" }}>
                  <div style={{
                    color: "#ffcc00",
                    fontSize: "14px",
                    marginBottom: "8px"
                  }}>
                    Authentication is taking longer than expected.
                  </div>
                  <button
                    onClick={handleForceLogout}
                    style={{
                      width: "200px",
                      height: "45px",
                      color: "#0f1a2c",
                      background: "#78bcfb",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontFamily: "Maven Pro, Arial, sans-serif",
                    }}
                  >
                    Logout & Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <TextCopyright />
      </>
    );
  }

  return (
    <>
      <div className="app-container">
        <div className="content">
          {!authRequired || isAuthenticated ? (
            <PanelScheduler
              user={user}
              idToken={idToken}
              getToken={getToken}
              onLogout={handleLogout}
            />
          ) : (
            <div className="login-screen">
              <LoginComponent 
                title="Web Scheduler"
                logoUrl={authConfig.imageUrl}
                isFirebaseEnabled={authConfig.firebase.enabled}
                isAzureEnabled={authConfig.azure.enabled}
                onLoginSuccess={async () => {/* handled by hook */}}
                onAzureLogin={handleAzureLogin}
                onFirebaseLogin={handleFirebaseLogin}
              />
            </div>
          )}
        </div>

        <TextCopyright />
      </div>
    </>
  );
};

export default App;